import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import ContentContainer from '../../areas/layout/content-container'
import { Link as RouterLink } from 'react-router-dom'
import MovieFilterIcon from '@mui/icons-material/MovieFilter'
import FavoriteIcon from '@mui/icons-material/Favorite'
import HistoryIcon from '@mui/icons-material/History'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import DevicesIcon from '@mui/icons-material/Devices'
import StarHalfIcon from '@mui/icons-material/StarHalf'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'

const PageWrapper = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(8),
}))

const HeroSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(8),
}))

const Logo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}))

const LogoIcon = styled(MovieFilterIcon)(({ theme }) => ({
  fontSize: 48,
  color: theme.palette.brand.main,
  marginBottom: theme.spacing(2),
}))

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 42,
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: 32,
  },
}))

const Tagline = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  color: theme.palette.text.secondary,
  maxWidth: 600,
  margin: '0 auto',
  lineHeight: 1.6,
  [theme.breakpoints.down('md')]: {
    fontSize: 16,
  },
}))

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(6),
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(3),
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    fontSize: 20,
    textAlign: 'center',
  },
}))

const SectionText = styled(Typography)(({ theme }) => ({
  fontSize: 15,
  color: theme.palette.text.secondary,
  lineHeight: 1.8,
  maxWidth: 800,
  textAlign: 'center',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    fontSize: 14,
  },
}))

const SectionTextSpaced = styled(SectionText)(({ theme }) => ({
  marginTop: theme.spacing(2),
}))

const GuideLink = styled(RouterLink)({
  color: 'inherit',
})

const FeaturesGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: theme.spacing(3),
  marginTop: theme.spacing(4),
}))

const FeatureCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
}))

const FeatureIcon = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  backgroundColor: theme.palette.brand.main + '20',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  '& svg': {
    fontSize: 24,
    color: theme.palette.brand.main,
  },
}))

const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}))

const FeatureDescription = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
}))

const features = [
  {
    icon: <HistoryIcon />,
    title: 'Watch History',
    description:
      'Track every episode and movie you watch. Progress bars show exactly where you are in every show, season by season.',
  },
  {
    icon: <PlaylistAddCheckIcon />,
    title: 'Watchlist',
    description:
      'Keep track of what you want to watch next - and get a reminder when a watchlisted movie hits theaters.',
  },
  {
    icon: <FavoriteIcon />,
    title: 'Favorites',
    description:
      'Save your favorite movies and TV shows to easily find them later. Build your personal collection of must-watch content.',
  },
  {
    icon: <StarHalfIcon />,
    title: 'Ratings & Comments',
    description:
      'Rate what you watch on a 1-10 scale with half-star steps, comment on movies, shows, and episodes, and reply to other members.',
  },
  {
    icon: <VisibilityOffIcon />,
    title: 'Spoiler Protection',
    description:
      'Unwatched episode stills and overviews stay blurred until you mark them watched - binge at your own pace, spoiler-free.',
  },
  {
    icon: <NotificationsActiveIcon />,
    title: 'Notifications',
    description:
      'Email and push alerts for new episodes, seasons, and finales of tracked shows - with per-type toggles, quiet hours, and digests.',
  },
  {
    icon: <TrendingUpIcon />,
    title: 'Statistics & Recaps',
    description:
      'View your watching statistics any time, plus monthly recap and year-in-review emails that wrap it all up for you.',
  },
  {
    icon: <MovieFilterIcon />,
    title: 'Discover',
    description:
      'Explore trending, popular, and top-rated titles, filter by streaming service, pick a mood for instant matches, and jump back to recently viewed titles.',
  },
  {
    icon: <CasinoRoundedIcon />,
    title: 'Randomizer',
    description:
      'Build a pool from your watchlist, favorites, or a mood, then spin to pick tonight\'s watch. One veto allowed - the second spin is final.',
  },
  {
    icon: <EmojiEventsIcon />,
    title: 'Badges',
    description:
      'Earn milestone badges as you watch, and see exactly how far the next one is with progress bars on your own profile.',
  },
  {
    icon: <DevicesIcon />,
    title: 'Cross-Platform',
    description:
      'The website and the iOS app share the same account, library, and settings - track your watching anywhere.',
  },
]

const AboutPage = () => {
  return (
    <ContentContainer>
      <PageWrapper>
        <HeroSection>
          <Logo>
            <LogoIcon />
            <Title>Movieception</Title>
          </Logo>
          <Tagline>
            Your personal movie and TV show tracker. Keep track of what you
            watch, discover new content, and never lose track of your favorite
            shows again.
          </Tagline>
        </HeroSection>

        <Section>
          <SectionTitle>What is Movieception?</SectionTitle>
          <SectionText>
            Movieception is a modern movie and TV show tracking application
            designed to help you organize your watching habits. Whether you're
            binge-watching a new series or slowly working through your movie
            backlog, Movieception keeps everything organized in one place.
          </SectionText>
          <SectionTextSpaced>
            Create an account to start tracking your favorites, build a
            watchlist of content you want to see, and log your watch history
            episode by episode. With detailed statistics and progress tracking,
            you'll always know exactly where you are in every show.
          </SectionTextSpaced>
        </Section>

        <Section>
          <SectionTitle>Features</SectionTitle>
          <FeaturesGrid>
            {features.map((feature) => (
              <FeatureCard key={feature.title}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Section>

        <Section>
          <SectionText>
            Want the full tour? See everything you can do on the{' '}
            <GuideLink to='/guide'>
              Guide page
            </GuideLink>
            .
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>Data Source</SectionTitle>
          <SectionText>
            Movie and TV show data is provided by The Movie Database (TMDB) Api
          </SectionText>
        </Section>
      </PageWrapper>
    </ContentContainer>
  )
}

export default AboutPage
